import React, { FunctionComponent, ReactNode, useCallback } from 'react';

import { useMutation } from '@apollo/client';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Container from '@mui/material/Container';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { createStyles, makeStyles } from '@mui/styles';

import { MUTATION_CREATE_EXPERIMENT } from 'client/app/apps/experiments/gql/mutations';
import { QUERY_EXPERIMENTS } from 'client/app/apps/experiments/gql/queries';
import { NEW_EXPERIMENT_NAME } from 'client/app/components/NavigationSidepanel';
import { experimentsRoutes } from 'client/app/lib/nav/actions';
import Colors from 'common/ui/Colors';
import { useNavigation } from 'common/ui/components/navigation/useNavigation';
import { ExampleWorkflowIcon } from 'common/ui/icons';
import { NewExperimentsIcon } from 'common/ui/icons/NewExperimentsIcon';

export const NoExperimentsMessage: FunctionComponent = () => {
  const [createExperimentMutation] = useMutation(MUTATION_CREATE_EXPERIMENT);
  const navigation = useNavigation();
  const classes = useStyles();

  const handleNewExperimentButtonClick = useCallback(async () => {
    const { data } = await createExperimentMutation({
      variables: { name: NEW_EXPERIMENT_NAME },
      refetchQueries: [{ query: QUERY_EXPERIMENTS }],
    });
    const newExperimentId = data?.createExperiment?.id;
    if (newExperimentId) {
      navigation.navigate(experimentsRoutes.detail, {
        id: newExperimentId,
      });
    }
  }, [createExperimentMutation, navigation]);

  const handleExampleGalleryClick = useCallback(async () => {
    navigation.navigate(experimentsRoutes.exampleGallery, undefined);
  }, [navigation]);

  return (
    <NoExperimentsCard
      content={
        <>
          <Button
            size="small"
            color="inherit"
            variant="contained"
            startIcon={<NewExperimentsIcon />}
            onClick={() => handleNewExperimentButtonClick()}
            className={classes.buttonExperiments}
          >
            New Experiment
          </Button>
          <Button
            size="small"
            color="inherit"
            variant="outlined"
            startIcon={<ExampleWorkflowIcon />}
            onClick={() => handleExampleGalleryClick()}
            className={classes.buttonExampleGallery}
          >
            Tutorials
          </Button>
        </>
      }
    />
  );
};

const NoExperimentsCard = ({ content }: { content: ReactNode }) => {
  const classes = useStyles();
  return (
    <Container className={classes.container}>
      <Box my={4} className={classes.box}>
        <Card className={classes.card}>
          <CardMedia className={classes.cardMedia} />
          <CardContent className={classes.cardContent}>
            <Typography variant="h2" className={classes.cardContentText}>
              It looks like you haven&apos;t created any experiments yet.
            </Typography>
            <Typography variant="h3" className={classes.cardContentText} component="p">
              You can use experiments to curate and organise the workflows and data that
              make up your experimental campaign.
            </Typography>
          </CardContent>
          <CardActions className={classes.cardContentButton}>{content}</CardActions>
        </Card>
      </Box>
    </Container>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: theme.spacing(8),
    },
    card: {
      maxWidth: '890px',
      height: '230px',
      borderRadius: '12px',
      position: 'relative',
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='457' height='235' viewBox='0 0 457 235' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath opacity='0.45' d='M81.129 180.24C72.8091 180.534 64.3576 182.173 56.0275 185.331C31.5434 194.582 15.622 215.291 5.93565 238.824C-2.83977 260.16 -2.25267 283.602 10.1665 303.491C21.1788 321.112 39.2762 333.592 58.8615 340.526C73.8819 345.85 90.1473 348.157 105.886 345.657C121.625 343.157 136.767 335.627 147.142 323.531C162.547 305.586 167.314 278.43 165.452 255.413C161.91 211.475 123.215 178.732 81.129 180.24Z' fill='url(%23paint0_linear_327_12150)'/%3E%3Cpath opacity='0.45' d='M127.574 214.933C177.866 126.172 305.781 68.1475 394.513 118.456C483.245 168.765 499.162 308.338 448.87 397.099C398.577 485.861 310.754 473.105 222.022 422.797C133.29 372.487 77.2813 303.695 127.574 214.933Z' fill='url(%23paint1_linear_327_12150)'/%3E%3Cpath opacity='0.45' d='M523.06 27.4153C521.219 25.4455 519.33 23.4991 517.407 21.5876C485.775 -9.7068 442.289 -32.0616 396.764 -24.5323C360.562 -18.5415 327.403 7.96261 317.648 43.3364C309.267 73.7217 318.207 106.59 333.592 134.096C366.565 193.083 427.417 232.63 491.917 252.77C520.974 261.838 552.187 267.421 581.814 260.451C598.213 256.593 613.995 248.691 625.172 236.103C636.35 223.504 642.422 205.904 639.088 189.4C635.685 172.57 623.505 159.132 612.34 146.078C596.046 127.045 580.964 107.009 566.453 86.5891C552.28 66.6353 539.727 45.2828 523.06 27.4036' fill='url(%23paint2_linear_327_12150)'/%3E%3Cdefs%3E%3ClinearGradient id='paint0_linear_327_12150' x1='175.942' y1='166.722' x2='49.7504' y2='388.559' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%23054CB2'/%3E%3Cstop offset='1' stop-color='%23910FF5'/%3E%3C/linearGradient%3E%3ClinearGradient id='paint1_linear_327_12150' x1='499.635' y1='70.4987' x2='227.869' y2='558.25' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%23054CB2'/%3E%3Cstop offset='1' stop-color='%23910FF5'/%3E%3C/linearGradient%3E%3ClinearGradient id='paint2_linear_327_12150' x1='659.845' y1='-49.4272' x2='454.563' y2='357.858' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%23054CB2'/%3E%3Cstop offset='1' stop-color='%23910FF5'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundColor: '#140C39',
      backgroundPosition: 'right',
    },
    cardMedia: {
      position: 'absolute',
      top: 0,
      right: 0,
      height: '100%',
      width: '100%',
    },
    cardContent: {
      position: 'relative',
      backgroundColor: 'transparent',
      paddingTop: theme.spacing(8),
      paddingLeft: theme.spacing(10),
    },
    cardContentText: {
      color: Colors.PRIMARY_CONTRAST,
      maxWidth: '500px',
    },
    cardContentButton: {
      paddingBottom: theme.spacing(12),
      paddingLeft: theme.spacing(10),
      border: 'none',
    },
    buttonExperiments: {
      color: Colors.TEXT_PRIMARY,
    },
    buttonExampleGallery: {
      backgroundColor: 'transparent',
      color: Colors.GREY_20,
    },
    box: {
      width: 'inherit',
      display: 'inherit',
      flexDirection: 'inherit',
      alignItems: 'inherit',
    },
  }),
);
